import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import Lottie from "react-lottie";

import animationData from "./animations/animation.json";
import Text from "./text/Text";
import {
  addToContacts,
  paymentReceive,
  paymentSent,
  poweredBy,
  sats,
} from "./messages";
import {
  displayTaprootCurrency,
  getAmountFormatted,
  getCurrencyAmount,
  satsToBtcAmount,
} from "./constants";
import { logoWithName } from "./images";
import { CustomLink } from "./link/Link";
import TruncatedTextTooltip from "./TruncatedTextTooltip";
import AddToContactDrawer from "./settings/contacts/AddToContactDrawer";

function PaidPaymentPage({ isFromBrowser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { showBtcAmount } = useSelector((state) => state.common);

  const data = location?.state?.details;
  const paymentType = location?.state?.type;
  const note = location?.state?.note;
  const withdrawRequest = location?.state?.withdraw_request || null;

  const isLightning = withdrawRequest
    ? ["lnbc", "lntb", "lnurl"].some((elem) => withdrawRequest.includes(elem))
    : false;

  const [openAddToContactModal, setOpenAddToContactModal] = useState(false);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  useEffect(() => {
    if (!openAddToContactModal) {
      const timeout = setTimeout(() => {
        navigate("/wallet");
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [openAddToContactModal]);

  return (
    <Box
      className="paid-payment-wrapper"
      sx={{
        backgroundColor: data?.target_currency === sats ? "#FEF6EB" : "#ffffff",
      }}
    >
      {withdrawRequest && !isLightning ? (
        <Box className="paid-payment-header">
          <TruncatedTextTooltip
            textProps={{ className: "default-text color-white" }}
            textValue={withdrawRequest}
            cellWidth="200px"
            fontSize={14}
          />
          <CustomLink
            onClick={() => {
              setOpenAddToContactModal(true);
            }}
          >
            <Text size={14} className="add-contact-text">
              {addToContacts}
            </Text>
          </CustomLink>
        </Box>
      ) : (
        <CloseIcon
          sx={{
            color: "#848B9E",
            height: "24px",
            width: "24px",
            cursor: "pointer",
          }}
          className="close-icon"
          onClick={() => navigate("/dashboard")}
        />
      )}
      <Lottie options={defaultOptions} height="110px" width="110px" />
      <Box className="paid-payment-detail" textAlign="center">
        <Text size={20} className="default-text txt-uppercase" font="regular">
          {paymentType === "payment" ? paymentReceive : paymentSent}
        </Text>
        <Text size={44} className="default-text txt-uppercase" font="bold">
          {getAmountFormatted(
            getCurrencyAmount(data?.target_amount_paid, data?.target_currency),
            data?.target_currency
          )}
        </Text>
        <Text size={24} className="txt-uppercase grey-text" font="semibold">
          {displayTaprootCurrency(
            data?.target_amount_paid,
            data?.target_currency
          )}
        </Text>

        {(data?.statement_descriptor || note) && (
          <Text
            size={16}
            className="default-text"
            font="regular"
            sx={{ wordBreak: "break-word" }}
          >
            {data?.statement_descriptor || note}
          </Text>
        )}
        <Box
          display="flex"
          position="absolute"
          alignItems="center"
          bottom="25px"
        >
          <Text
            size={!isFromBrowser ? 12 : 14}
            className="default-text"
            font="semibold"
          >
            {poweredBy}
          </Text>
          <img
            src={logoWithName}
            alt="logo"
            style={{
              marginLeft: "12px",
              ...(!isFromBrowser && { height: "20px" }),
            }}
          />
        </Box>
      </Box>
      {withdrawRequest && (
        <AddToContactDrawer
          openDrawer={openAddToContactModal}
          setOpenDrawer={setOpenAddToContactModal}
          withdrawRequest={withdrawRequest}
        />
      )}
    </Box>
  );
}

export default PaidPaymentPage;
